import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import moment from 'moment';

import type { Billing, Service } from 'webapp-types';
import { BillingFeature } from 'webapp/enums/enums';

import AppNotificationsPausedOrDiscountedComponent from 'webapp/components/app-notifications/paused-or-discounted';
import BillingAddonsComponent from 'webapp/components/billing/addons';
import BillingAiCreditsInfoComponent from 'webapp/components/billing/ai-credits/info';
import BillingAiCreditsWarningBannerComponent from 'webapp/components/billing/ai-credits/warning-banner';
import BillingFeaturesComponent from 'webapp/components/billing/features';
import BillingInvoicesComponent from 'webapp/components/billing/invoices';
import BillingOnlyAdminsSettingsComponent from 'webapp/components/billing/only-admins-settings';
import BillingPaymentDetailsComponent from 'webapp/components/billing/payment-details';
import BillingServicesComponent from 'webapp/components/billing/services';
import BillingSubscriptionInfoComponent from 'webapp/components/billing/subscription-info';
import BillingTrialBoxComponent from 'webapp/components/billing/trial-box';
import BillingUnverifiedAgencyWarningComponent from 'webapp/components/billing/unverified-agency-warning';

import type { TrackingOriginForSubscriptionChangePopup } from 'webapp/services/billing';

type Args = Record<string, never>;

export default class BillingOverviewComponent extends Component {
	@service('billing') declare billingService: Service.Billing;
	@service('request') declare requestService: Service.Request;
	@service('popup') declare popupService: Service.Popup;
	@service('flow/cancellation') declare flowCancellationService: Service.Flow.Cancellation;
	@service('account') declare accountService: Service.Account;

	@tracked invoices: Billing.Invoice[] = [];

	trackingOrigin: TrackingOriginForSubscriptionChangePopup = 'billing_page';

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.billingService
			.getInvoices()
			.then((invoices) => {
				this.invoices = invoices;
			})
			.catch(this.requestService.handleGenericThrownError.bind(this.requestService));
	}

	get showTrialBox() {
		return !this.billingService.hadTrial && this.billingService.canRequestTrial;
	}

	get showBannerForMigration() {
		return false;
	}

	get showAiCreditsWarningBanner() {
		return !this.billingService.checkIfCan(BillingFeature.AI_CREDITS);
	}

	get showAiCreditsPaymentInfo() {
		return this.billingService.extraAiCreditsMonthlyPriceCents > 0;
	}

	get showInvoices() {
		return this.invoices.length > 0;
	}

	get isFirstMonthPaying() {
		const timestamp = this.billingService.becamePaying;
		return timestamp && moment().diff(moment.unix(timestamp), 'months') < 1;
	}

	get showCancelSubscription() {
		const { isCanceled, isTrial, activePlan } = this.billingService;
		return !activePlan?.isFree && !isTrial && !isCanceled;
	}

	get isCancelSubscriptionBlocked() {
		return this.billingService.blockDowngradeOrCancelDueCommitment;
	}

	@action
	changePayment() {
		this.billingService.changePayment();
	}

	@action
	cancelSubscription() {
		this.billingService.startCancellation(this.trackingOrigin);
	}

	@action
	closeBannerForMigration() {
		this.billingService.skipBannerForMigration = true;
	}
	<template>
		<div class="overview">

			{{#if this.flowCancellationService.billingNotificationText}}
				<AppNotificationsPausedOrDiscountedComponent>
					{{this.flowCancellationService.billingNotificationText}}
				</AppNotificationsPausedOrDiscountedComponent>
			{{else if this.billingService.showUnverifiedAgencyWarning}}
				<BillingUnverifiedAgencyWarningComponent />
			{{/if}}
			{{!--
    {{else if this.showBannerForMigration}}
        <div class="subscription-changes-notification">
            <p></p>
            <a role="button" class="close" {{on "click" this.closeBannerForMigration}}>
            	<Common::UiIcon @name="Close" @size="sm" />
            </a>
        </div>
    {{/if}}
    --}}

			{{#if this.showAiCreditsWarningBanner}}
				<BillingAiCreditsWarningBannerComponent />
			{{/if}}

			<div class="column__wrapper">
				<div class="column">
					<div class="row">
						<BillingSubscriptionInfoComponent />
					</div>
					{{#if this.showAiCreditsPaymentInfo}}
						<div class="row">
							<BillingAiCreditsInfoComponent />
						</div>
					{{/if}}
				</div>

				<div class="column">
					<div class="row">
						{{#if this.showTrialBox}}
							<BillingTrialBoxComponent />
						{{else}}
							<BillingPaymentDetailsComponent
								@onChangePayment={{this.changePayment}}
							/>
						{{/if}}
					</div>
					{{#if this.showInvoices}}
						<div class="row">
							<BillingInvoicesComponent @model={{this.invoices}} />
						</div>
					{{/if}}
				</div>

				<div class="column">
					<div class="row {{if this.isFirstMonthPaying 'second'}}">
						<BillingAddonsComponent
							@trackingOrigin={{this.trackingOrigin}}
							@showHelp={{true}}
							@layout="list"
						/>
					</div>
					<div class="row {{if this.isFirstMonthPaying 'first'}}">
						<BillingServicesComponent />
					</div>
				</div>

				<div class="column">
					<div class="row">
						<BillingFeaturesComponent />
						<BillingOnlyAdminsSettingsComponent />
					</div>
				</div>
			</div>

			{{#if this.showCancelSubscription}}
				<div class="row last-row controls2">
					{{#if this.isCancelSubscriptionBlocked}}
						<div class="controls2__left">
							<p>
								Cancellation isn’t available during your commitment period.
								<a
									class="link link--secondary"
									href={{this.billingService.annualCommitmentCallUrl}}
									target="_blank"
								>
									Talk to our team
								</a>
							</p>
						</div>
					{{else}}
						<div class="controls2__left">
							<a role="button" class="cancel" {{on "click" this.cancelSubscription}}>
								Cancel subscription
							</a>
						</div>
						<div class="controls2__right">
							<a
								class="link link--secondary link--underline"
								href="https://databox.com/terms-of-service"
								target="_blank"
							>
								Terms of Service
							</a>
						</div>
					{{/if}}
				</div>
			{{/if}}

		</div>
	</template>
}
